import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { fieldErrors, issuesOf } from '../../api/errors';
import { itemApi, orgApi, refApi } from '../../api/endpoints';
import {
  categoriesQuery,
  itemQuery,
  queryKeys,
  taxesQuery,
  taxTypesQuery,
} from '../../api/queries';
import type {
  EtaItemCode,
  EtaItemType,
  ItemKind,
  OrganizationTax,
  StatementType,
  TableGoodsType,
  TaxGoodsType,
  UnitType,
} from '../../api/types';
import { useAccess } from '../../app/access';
import { useI18n } from '../../i18n/I18nProvider';
import { Button } from '../../ui/Button';
import { Checkbox, CheckboxGroup, Segmented } from '../../ui/Choice';
import { AsyncComboBox } from '../../ui/ComboBox';
import { cx } from '../../ui/cx';
import { DecimalField } from '../../ui/DecimalField';
import { ModalDialog } from '../../ui/Dialog';
import { Callout } from '../../ui/Feedback';
import { FormErrors } from '../../ui/FormErrors';
import { SelectField } from '../../ui/Select';
import { TextField } from '../../ui/TextField';
import { useToast } from '../../ui/Toast';
import { describeFrom, optionalText, summaryItems } from '../forms';
import styles from '../sales/Sales.module.css';

export interface SavedItem {
  id: string;
}

type TaxOption = Pick<
  OrganizationTax,
  'id' | 'taxType' | 'taxSubtype' | 'rate' | 'nameAr' | 'nameEn'
>;

/**
 * The catalogue item form (T.02/T.03/T.04/T.06 create, M-05 edit), one form for three containers
 * (DESIGN_SYSTEM §6a): the catalogue sheet, the edit-item sheet and the Today dialog. Groups, in
 * order: ETA item code; identity; classification (stored for reports only, never used by the tax
 * engine); default taxes as a checkbox list of the organization's taxes, plus an
 * organization-specific tax (settings.manage) validated against the ETA catalogue; default
 * discounts. The form mounts empty (create) or loads the item once (edit).
 */
export function ItemForm({
  orgId,
  itemId,
  onSaved,
  onCancel,
  className,
}: {
  orgId: string;
  /** Edit this item; null = create. */
  itemId: string | null;
  onSaved: (item: SavedItem) => void;
  onCancel: () => void;
  className?: string;
}) {
  const { t, pick, formatPercent } = useI18n();
  const toast = useToast();
  const access = useAccess(orgId);
  const queryClient = useQueryClient();
  const existing = useQuery({ ...itemQuery(orgId, itemId ?? ''), enabled: Boolean(itemId) });
  const taxes = useQuery(taxesQuery(orgId));
  const categories = useQuery(categoriesQuery(orgId, 'item'));

  const [nameAr, setNameAr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [internalCode, setInternalCode] = useState('');
  const [unit, setUnit] = useState<UnitType | null>(null);
  const [itemKind, setItemKind] = useState<ItemKind>('goods');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [discountMode, setDiscountMode] = useState<'rate' | 'amount'>('rate');
  const [discount, setDiscount] = useState('');
  const [itemsDiscount, setItemsDiscount] = useState('');
  const [valueDifference, setValueDifference] = useState('');
  const [etaType, setEtaType] = useState<EtaItemType>('EGS');
  const [etaCode, setEtaCode] = useState('');
  const [pickedCode, setPickedCode] = useState<EtaItemCode | null>(null);
  const [taxIds, setTaxIds] = useState<string[]>([]);
  const [taxGoodsType, setTaxGoodsType] = useState<TaxGoodsType>('general');
  const [tableGoodsType, setTableGoodsType] = useState<TableGoodsType>('none');
  const [statementType, setStatementType] = useState<StatementType>('goods');
  const [customOpen, setCustomOpen] = useState(false);

  // Edit: fill the form once from the stored item.
  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    const i = existing.data;
    if (!itemId || !i || loadedFor.current === itemId) return;
    loadedFor.current = itemId;
    setNameAr(i.nameAr);
    setNameEn(i.nameEn ?? '');
    setDescription(i.description ?? '');
    setPrice(i.unitPrice);
    setInternalCode(i.internalCode);
    setUnit({
      code: i.unitCode,
      nameEn: i.unitNameEn,
      nameAr: i.unitNameAr,
      ambiguous: i.unitAmbiguous,
    });
    setItemKind(i.itemKind);
    setCategoryId(i.categoryId);
    setDiscountMode(i.defaultDiscountAmount ? 'amount' : 'rate');
    setDiscount(i.defaultDiscountAmount ?? i.defaultDiscountRate ?? '');
    setItemsDiscount(i.defaultItemsDiscount ?? '');
    setValueDifference(i.defaultValueDifference ?? '');
    setEtaType(i.etaItemType ?? 'EGS');
    setEtaCode(i.etaItemCode ?? '');
    setTaxIds(i.defaultTaxes.map((x) => x.id));
    setTaxGoodsType(i.taxGoodsType);
    setTableGoodsType(i.tableGoodsType);
    setStatementType(i.statementType);
  }, [itemId, existing.data]);

  // Create: VAT (the first active T1 tax) is ticked by default, once the taxes are known.
  const defaultsSet = useRef(false);
  useEffect(() => {
    if (itemId || defaultsSet.current || !taxes.data) return;
    defaultsSet.current = true;
    setTaxIds(
      taxes.data
        .filter((x) => x.taxType === 'T1' && x.isActive)
        .slice(0, 1)
        .map((x) => x.id),
    );
  }, [itemId, taxes.data]);

  const save = useMutation({
    mutationFn: async () => {
      const defaultDiscount = discount
        ? discountMode === 'rate'
          ? { rate: discount }
          : { amount: discount }
        : undefined;
      if (itemId) {
        await itemApi.update(orgId, itemId, {
          version: existing.data?.version ?? 1,
          nameAr: nameAr.trim(),
          nameEn: optionalText(nameEn) ?? null,
          description: optionalText(description) ?? null,
          unitPrice: price || '0',
          ...(unit ? { unitCode: unit.code } : {}),
          categoryId,
          defaultDiscount: defaultDiscount ?? null,
          defaultItemsDiscount: itemsDiscount || null,
          defaultValueDifference: valueDifference || null,
        });
        return { id: itemId };
      }
      return itemApi.create(orgId, {
        internalCode: internalCode.trim(),
        nameAr: nameAr.trim(),
        ...(optionalText(nameEn) ? { nameEn: nameEn.trim() } : {}),
        ...(optionalText(description) ? { description: description.trim() } : {}),
        itemKind,
        isStockable: false,
        etaItemType: etaType,
        etaItemCode: etaCode.trim(),
        unitCode: unit?.code ?? '',
        unitPrice: price || '0',
        currency: 'EGP',
        taxGoodsType,
        tableGoodsType,
        statementType,
        defaultTaxIds: taxIds,
        ...(categoryId ? { categoryId } : {}),
        ...(defaultDiscount ? { defaultDiscount } : {}),
        ...(itemsDiscount ? { defaultItemsDiscount: itemsDiscount } : {}),
        ...(valueDifference ? { defaultValueDifference: valueDifference } : {}),
      });
    },
    onSuccess: async (saved) => {
      toast.success(itemId ? t.catalogue.updated : t.catalogue.created);
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.org(orgId), 'items'] });
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.org(orgId), 'sales'] });
      onSaved({ id: saved.id });
    },
  });

  const issues = issuesOf(save.error);
  const errors = fieldErrors(t, issues);
  const labels: Record<string, string> = {
    nameAr: t.catalogue.nameAr,
    internalCode: t.catalogue.internalCode,
    unitCode: t.catalogue.unit,
    unitPrice: t.catalogue.price,
    etaItemCode: t.catalogue.etaCode,
    defaultTaxIds: t.catalogue.taxes,
  };
  // The organization's active taxes; in edit mode also the item's own defaults, shown read-only.
  const taxOptions: TaxOption[] = [...(taxes.data ?? []).filter((x) => x.isActive)];
  for (const own of existing.data?.defaultTaxes ?? [])
    if (!taxOptions.some((x) => x.id === own.id)) taxOptions.push(own);

  return (
    <>
      <form
        className={cx(styles.formStack, className)}
        data-testid="item-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.isError ? (
          <FormErrors
            items={summaryItems(
              t,
              issues.length ? issues : [{ path: '(root)', issue: 'unknown' }],
              describeFrom(labels),
            )}
          />
        ) : null}
        <h3 className={styles.subsection}>{t.catalogue.etaCode}</h3>
        <div className={styles.fieldGrid}>
          <LabeledSegment label={t.catalogue.etaType}>
            <Segmented<EtaItemType>
              aria-label={t.catalogue.etaType}
              value={etaType}
              onChange={setEtaType}
              options={[
                { value: 'EGS', label: 'EGS' },
                { value: 'GS1', label: 'GS1' },
              ]}
              isDisabled={Boolean(itemId)}
            />
          </LabeledSegment>
          <AsyncComboBox<EtaItemCode>
            label={t.catalogue.codeSearch}
            fieldName="etaCodeSearch"
            value={pickedCode?.id ?? null}
            selectedItem={pickedCode}
            onChange={(c) => {
              setPickedCode(c);
              if (!c) return;
              setEtaType(c.itemType);
              setEtaCode(c.itemCode);
            }}
            queryFor={(q) => ({
              queryKey: [...queryKeys.org(orgId), 'eta-codes', q],
              queryFn: ({ signal }) => orgApi.etaItemCodes(orgId, q, signal),
            })}
            getKey={(c) => c.id}
            getLabel={(c) => `${c.itemType} ${c.itemCode}`}
            isDisabled={Boolean(itemId)}
          />
          <TextField
            label={t.catalogue.etaCode}
            description={t.catalogue.etaCodeHint}
            fieldName="etaItemCode"
            value={etaCode}
            onChange={setEtaCode}
            dir="ltr"
            isRequired
            isDisabled={Boolean(itemId)}
            errorMessage={errors.etaItemCode}
          />
        </div>

        <div className={styles.fieldGrid}>
          <TextField
            label={t.catalogue.nameAr}
            fieldName="nameAr"
            value={nameAr}
            onChange={setNameAr}
            isRequired
            errorMessage={errors.nameAr}
          />
          <TextField
            label={t.catalogue.nameEn}
            fieldName="nameEn"
            value={nameEn}
            onChange={setNameEn}
            isOptional
            dir="ltr"
          />
          <DecimalField
            label={t.catalogue.price}
            fieldName="unitPrice"
            value={price}
            onChange={setPrice}
            isRequired
            errorMessage={errors.unitPrice}
          />
          <TextField
            label={t.catalogue.internalCode}
            fieldName="internalCode"
            value={internalCode}
            onChange={setInternalCode}
            isRequired
            dir="ltr"
            isDisabled={Boolean(itemId)}
            errorMessage={errors.internalCode}
          />
          <AsyncComboBox<UnitType>
            label={t.catalogue.unit}
            fieldName="unitCode"
            value={unit?.code ?? null}
            selectedItem={unit}
            onChange={setUnit}
            queryFor={(q) => ({
              queryKey: ['ref', 'unit-types', q],
              queryFn: ({ signal }) => refApi.unitTypes(q, signal),
            })}
            getKey={(u) => u.code}
            getLabel={(u) => `${u.code} — ${pick(u.nameAr, u.nameEn)}`}
            isRequired
            errorMessage={errors.unitCode}
          />
          <SelectField
            label={t.catalogue.category}
            fieldName="categoryId"
            value={categoryId ?? '__none'}
            onChange={(v) => setCategoryId(v && v !== '__none' ? v : null)}
            options={[
              { id: '__none', label: t.parties.noCategory },
              ...(categories.data ?? []).map((c) => ({
                id: c.id,
                label: pick(c.nameAr, c.nameEn ?? c.nameAr),
              })),
            ]}
          />
        </div>
        {unit?.code === 'KG' ? (
          <Callout tone="warning" role="note">
            <span data-testid="kg-warning">{t.catalogue.kgWarning}</span>
          </Callout>
        ) : null}
        <TextField
          label={t.catalogue.description}
          fieldName="description"
          value={description}
          onChange={setDescription}
          isOptional
          multiline
          rows={2}
        />

        <h3 className={styles.subsection}>{t.catalogue.classification}</h3>
        {/* RI-10: the classification is fixed when the item is created, so it reads as read-only. */}
        {itemId ? <p className={styles.readOnlyNote}>{t.catalogue.readOnlyNote}</p> : null}
        <div className={cx(styles.fieldGrid, Boolean(itemId) && styles.readOnlyBlock)}>
          <SelectField
            label={t.catalogue.itemKind}
            fieldName="itemKind"
            value={itemKind}
            onChange={(v) => v && setItemKind(v as ItemKind)}
            isDisabled={Boolean(itemId)}
            options={(['goods', 'service'] as const).map((v) => ({
              id: v,
              label: t.catalogue.itemKinds[v],
            }))}
          />
          <SelectField
            label={t.catalogue.taxGoodsType}
            fieldName="taxGoodsType"
            value={taxGoodsType}
            onChange={(v) => v && setTaxGoodsType(v as TaxGoodsType)}
            isDisabled={Boolean(itemId)}
            options={(['general', 'table'] as const).map((v) => ({
              id: v,
              label: t.catalogue.taxGoodsTypes[v],
            }))}
          />
          <SelectField
            label={t.catalogue.tableGoodsType}
            fieldName="tableGoodsType"
            value={tableGoodsType}
            onChange={(v) => v && setTableGoodsType(v as TableGoodsType)}
            isDisabled={Boolean(itemId)}
            options={(['none', 'table_1', 'table_2'] as const).map((v) => ({
              id: v,
              label: t.catalogue.tableGoodsTypes[v],
            }))}
          />
          <SelectField
            label={t.catalogue.statementType}
            fieldName="statementType"
            value={statementType}
            onChange={(v) => v && setStatementType(v as StatementType)}
            isDisabled={Boolean(itemId)}
            options={(['goods', 'service', 'adjustment'] as const).map((v) => ({
              id: v,
              label: t.catalogue.statementTypes[v],
            }))}
          />
        </div>

        {/* Default taxes: a checkbox list of the organization's taxes (DV-24), read-only once saved. */}
        <h3 className={styles.subsection}>{t.catalogue.taxes}</h3>
        <CheckboxGroup
          label={<span className="visually-hidden">{t.catalogue.taxes}</span>}
          value={taxIds}
          onChange={setTaxIds}
          isReadOnly={Boolean(itemId)}
          fieldName="defaultTaxIds"
          testId="item-taxes"
          listClassName={cx(styles.taxChecks, Boolean(itemId) && styles.readOnlyBlock)}
        >
          {taxOptions.map((x) => (
            <Checkbox key={x.id} value={x.id}>
              <span className={styles.taxOption}>
                <span>{pick(x.nameAr, x.nameEn)}</span>
                <span className={`${styles.subtle} ${styles.mono}`}>
                  {x.rate ? `${formatPercent(x.rate)} · ` : ''}
                  {x.taxType}/{x.taxSubtype}
                </span>
              </span>
            </Checkbox>
          ))}
        </CheckboxGroup>
        {access.can('settings.manage') && !itemId ? (
          <Button
            size="sm"
            variant="ghost"
            icon={<Plus size={14} />}
            onPress={() => setCustomOpen(true)}
            testId="add-custom-tax"
            className={styles.inlineStart}
          >
            {t.catalogue.addCustomTax}
          </Button>
        ) : null}

        <div className={styles.fieldGrid}>
          <LabeledSegment label={t.editor.lines.discountMode}>
            <Segmented<'rate' | 'amount'>
              aria-label={t.editor.lines.discountMode}
              value={discountMode}
              onChange={setDiscountMode}
              options={[
                { value: 'rate', label: t.editor.lines.discountPercent },
                { value: 'amount', label: t.editor.lines.discountAmount },
              ]}
            />
          </LabeledSegment>
          <DecimalField
            label={t.catalogue.discount}
            fieldName="defaultDiscount"
            value={discount}
            onChange={setDiscount}
            isOptional
          />
          <DecimalField
            label={t.catalogue.itemsDiscount}
            fieldName="defaultItemsDiscount"
            value={itemsDiscount}
            onChange={setItemsDiscount}
            isOptional
          />
          <DecimalField
            label={t.catalogue.valueDifference}
            fieldName="defaultValueDifference"
            value={valueDifference}
            onChange={setValueDifference}
            isOptional
          />
        </div>

        <div className={styles.dialogFooter}>
          <Button variant="secondary" onPress={onCancel}>
            {t.common.cancel}
          </Button>
          <Button variant="primary" type="submit" isPending={save.isPending} testId="item-save">
            {itemId ? t.catalogue.save : t.catalogue.create}
          </Button>
        </div>
      </form>
      {/* Outside the form: its own form must not submit this one. */}
      <CustomTaxDialog
        orgId={orgId}
        isOpen={customOpen}
        onOpenChange={setCustomOpen}
        onCreated={(id) => setTaxIds((ids) => [...ids, id])}
      />
    </>
  );
}

/** T.03 "add custom tax": an organization tax validated against the official ETA tax catalogue. */
export function CustomTaxDialog({
  orgId,
  isOpen,
  onOpenChange,
  onCreated,
}: {
  orgId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}) {
  const { t, pick } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const types = useQuery({ ...taxTypesQuery, enabled: isOpen });
  const [taxType, setTaxType] = useState<string | null>(null);
  const [subtype, setSubtype] = useState<string | null>(null);
  const [rate, setRate] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const selected = types.data?.find((x) => x.code === taxType);
  const sub = selected?.subtypes.find((s) => s.code === subtype);
  const create = useMutation({
    mutationFn: () =>
      orgApi.createTax(orgId, {
        taxType: taxType ?? '',
        taxSubtype: subtype ?? '',
        ...(sub?.kind === 'rate' ? { rate: rate || '0' } : {}),
        nameAr: nameAr.trim(),
        nameEn: nameEn.trim(),
      }),
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.org(orgId), 'taxes'] });
      toast.success(t.setup.saved);
      onCreated(res.id);
      onOpenChange(false);
    },
  });
  const errors = fieldErrors(t, issuesOf(create.error));
  return (
    <ModalDialog isOpen={isOpen} onOpenChange={onOpenChange} title={t.catalogue.customTaxTitle}>
      <form
        className={styles.formStack}
        data-testid="custom-tax-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          create.mutate();
        }}
      >
        {create.isError && !Object.keys(errors).length ? (
          <Callout tone="danger">{t.errors.unknown}</Callout>
        ) : null}
        <SelectField
          label={t.catalogue.taxType}
          fieldName="taxType"
          value={taxType}
          onChange={(v) => {
            setTaxType(v);
            setSubtype(null);
          }}
          options={(types.data ?? []).map((x) => ({
            id: x.code,
            label: `${x.code} — ${pick(x.nameAr, x.nameEn)}`,
          }))}
          isRequired
          errorMessage={errors.taxType}
        />
        <SelectField
          label={t.catalogue.taxSubtype}
          fieldName="taxSubtype"
          value={subtype}
          onChange={setSubtype}
          options={(selected?.subtypes ?? []).map((s) => ({
            id: s.code,
            label: `${s.code} — ${pick(s.nameAr, s.nameEn)}`,
          }))}
          isRequired
          isDisabled={!selected}
          errorMessage={errors.taxSubtype}
        />
        {sub?.kind === 'rate' ? (
          <DecimalField
            label={t.catalogue.rate}
            fieldName="rate"
            value={rate}
            onChange={setRate}
            isRequired
            errorMessage={errors.rate}
          />
        ) : null}
        <TextField
          label={t.catalogue.taxNameAr}
          fieldName="nameAr"
          value={nameAr}
          onChange={setNameAr}
          isRequired
          errorMessage={errors.nameAr}
        />
        <TextField
          label={t.catalogue.taxNameEn}
          fieldName="nameEn"
          value={nameEn}
          onChange={setNameEn}
          isRequired
          dir="ltr"
          errorMessage={errors.nameEn}
        />
        <div className={styles.dialogFooter}>
          <Button variant="secondary" onPress={() => onOpenChange(false)}>
            {t.common.cancel}
          </Button>
          <Button
            variant="primary"
            type="submit"
            isPending={create.isPending}
            testId="custom-tax-save"
          >
            {t.catalogue.createTax}
          </Button>
        </div>
      </form>
    </ModalDialog>
  );
}

/** A visible caption above a segmented control (the group keeps its own aria-label). */
export function LabeledSegment({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <span aria-hidden="true" style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>
        {label}
      </span>
      {children}
    </div>
  );
}
